/* istanbul ignore file */
import {
  FocusSkillType,
  FocusSkillTypeIndex,
  InterestedRoleCareerJourneyStatus,
  CoursesSearchProduct,
  ReadFields,
} from 'models/enum';

export interface IEmployeeResponse {
  employee: IEmployee;
  employeeProfileFlags: IEmployeeProfileFlags;
}

export interface IEmployeeProfileFlags {
  prepolulateInterestedRoles: boolean;
  forceOpenToWork: boolean;
  assessmentFlags?: IAssessmentFlags;
}

interface IAssessmentFlags {
  tellUsAboutYourself: ITellUsAboutYourselfFlags;
}

interface ITellUsAboutYourselfFlags {
  isSkipped: boolean;
}

export interface IEmployee {
  id: number;
  clientEmployeeId: string;
  profile: IProfile;
  workExperiences: IWorkExperience[];
  degreesAndCertifications: IDegreeAndCertification[];
  competencies: ICompetency[];
  skills: ISkills;
  currentRole: ICurrentRole;
  profileCompleteness: IProfileCompleteness | null;
  resumeUrl: string;
  interestedRoles: any[];
}

export interface IProfile {
  profilePictureUrl: IEmployeeProfileImage;
  firstName: string;
  lastName: string;
  location: string;
  departmentName: string;
  subFunction: string;
  jobTitle: string;
  currentJobStartDate: string;
  jobCode: string;
  orgId: number;
}

export interface IWorkExperience {
  id: string;
  jobTitle?: string;
  company?: string;
  jobLocation?: string;
  isPermanentEmployee?: boolean | null;
  isFullTime?: boolean | null;
  weeklyHours?: number;
  startDate?: IDateMonthYear;
  endDate?: IDateMonthYear;
  description?: string;
}

export interface IWorkExperienceNotification {
  Id: string;
  JobTitle?: string;
  Company?: string;
  JobLocation?: string;
  IsPermanentEmployee?: boolean;
  IsFullTime?: boolean;
  WeeklyHours?: number;
  StartDate?: IDateMonthYear;
  EndDate?: IDateMonthYear;
  Description?: string;
}

export interface IDegreeAndCertification {
  id: string;
  institution?: string;
  certificationType?: string;
  fieldOfStudy?: string;
  startDate?: IDateMonthYear;
  endDate?: IDateMonthYear;
  details?: string;
}

export interface ICertificationNotification {
  Id: string;
  CertificationType?: string;
  Institution?: string;
  FieldOfStudy?: string;
  StartDate?: IDateMonthYear;
  EndDate?: IDateMonthYear;
  Details?: string;
}

export interface IDateMonthYear {
  month?: number;
  year?: number;
}

export interface ILocation {
  name: string;
  city: string;
  country: string;
  state: string;
  postalCode: string;
}
export interface IEmployeeFocus {
  focus: ISkill | ICompetency;
  skillType: FocusSkillType;
}

export interface IFocusSkill {
  id: string;
  name: string;
  type: FocusSkillType;
  product?: CoursesSearchProduct;
}

export interface ISearchFocusItem {
  id?: string | null;
  name: string;
  type: FocusSkillTypeIndex;
  product?: CoursesSearchProduct;
}

interface IExtendableSkill {
  id?: number | null;
  likelihood?: string | null;
  source?: any[];
}

export interface IBaseSkill extends IExtendableSkill {
  name: string;
}

//WAF(web application firewall) rules, name becomes optional due to PHP injection attack.
export interface IWAFSkill extends IExtendableSkill {
  name?: string | null;
}

export interface ISkill extends IBaseSkill, IExtendableSkill {
  likelyhood?: string;
  code?: string;
  assessmentStatus?: string;
}

export interface ISkills {
  mySkills: ISkill[];
  focusSkills?: ISkill[];
}

export interface ICumulativeSkills extends Array<ISkill> {
  id: number | string;
  name: string;
}

export interface ICurrentRole {
  openToWork: boolean;
  locationsInterested: string[];
  workRemote: boolean;
  isCareerJourneyActive?: boolean;
  careerJourneyStatus?: InterestedRoleCareerJourneyStatus | undefined;
  score?: number;
  skillGaps?: ISkill[];
  skillOverlaps?: ISkill[];
  competencyGaps?: ICompetency[] | undefined;
  subFunction?: string;
}
export interface IProfileCompleteness {
  workExperience: boolean;
  degreesAndCertifications: boolean;
  skills: boolean;
  resume: boolean;
  careerProgressionViewed: boolean;
  selfAssessmentTaken: boolean;
  resumeLastUpdatedOn?: string;
}

export enum AnalizedResumeStatus {
  idle = 'IDLE',
  partial = 'PARTIAL',
  completed = 'COMPLETED',
}

export interface IResumeUploadResponse {
  workExperiences: IWorkExperience[];
  degreesAndCertifications: IDegreeAndCertification[];
  waitingForSkills: boolean;
}

export type IResumePartiallyAnalized = Pick<
  IResumeUploadResponse,
  'workExperiences' | 'degreesAndCertifications'
>;

export interface IAnalizedResume extends IResumePartiallyAnalized {
  skills?: ISkill[];
}

export interface IProfileCompletion {
  analysisStatus: AnalizedResumeStatus;
  analized: IAnalizedResume;
  selected: Array<'skills' | 'workExperiences' | 'degreesAndCertifications'>;
  unnormSkills: Array<string> | null;
}

export interface IVideo {
  id: number;
  title: string;
  description: string;
  likes: number;
  url: string;
  durationInMinutes: string;
}

export interface ILateralMove {
  id: number;
  name: string;
}

export interface IRole {
  id: number;
  title: string;
  position: string;
  department: string;
  location: string;
  description: string;
  since?: string;
}

export interface ILevel {
  name: string;
  number: number | 'All';
}

export interface IAuthor {
  name: string;
  position: string;
}

export interface ICourse {
  id: number;
  title: string;
  description: string;
  studentsCount: number;
  lessonsCount: number;
  projectsCount: number;
  requiredLevel: ILevel;
  relatedSkills: string[];
  introductoryVideo: IVideo;
  author: IAuthor;
  posterImageUrl: string;
  lessons: IVideo[];
}

export interface IResumeNotification {
  degreesAndCertifications: ICertificationNotification[];
  workExperiences: IWorkExperienceNotification[];
  skills: ISkill[];
}

export interface IResume {
  degreesAndCertifications: IDegreeAndCertification[];
  workExperiences: IWorkExperience[];
  skills: ISkill[];
}

export interface INormalizedSkills {
  normskills: ISkill[];
  unnormskills: ISkill[];
}

export interface IEnrollmentPayload {
  skillId: string;
  skillType: FocusSkillTypeIndex;
  courseId: number;
}

export interface IEmployeeProfileImage {
  content: string;
}

export interface IAvatarColor {
  bgcolor: string;
  color: string;
}

export interface ISearchEmployee {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  avatarColors: IAvatarColor;
  jobTitle: string;
  relationship?: string;
  removed?: boolean;
  isIc2Executive?: boolean;
}

export interface IGlobalStorePayload {
  [ReadFields.DegreesAndCertificationsCompleted]?: boolean | null;
  [ReadFields.SkillsCompleted]?: boolean | null;
  [ReadFields.WorkExperienceCompleted]?: boolean | null;
}

export interface IGlobalStore {
  id?: number;
  orgId?: number;
  [ReadFields.CareerPreferences]: boolean;
  [ReadFields.MySkills]: boolean;
  [ReadFields.SuggestedSkills]: boolean;
  [ReadFields.InterestedRoles]: boolean;
  [ReadFields.AssessmentCompleted]: boolean;
  [ReadFields.DegreesAndCertificationsCompleted]: boolean;
  [ReadFields.NextBestRoleCompleted]: boolean;
  [ReadFields.ResumeCompleted]: string;
  [ReadFields.SkillsCompleted]: boolean;
  [ReadFields.WorkExperienceCompleted]: boolean;
  [ReadFields.CareerProgressionViewed]: boolean;
}

interface IExtendableCompetency {
  id?: number;
  code?: string;
  globalCode?: string;
}

export interface ICompetency extends IExtendableCompetency {
  name: string;
  description?: string;
}

export interface IWAFCompetency extends IExtendableCompetency {
  name?: string | null;
  description?: string | null;
}

export interface ISuccessProfile {
  profile: ISuccessProfileData;
}

export interface ISuccessProfileData {
  jobId: string;
  jobTitle: string;
  skills: { normsSkills: ISkill[] };
  behavioralCompetencies: ICompetency[];
  isTechnical: boolean;
}

export interface INormSkillsToAdd {
  skillName: string[] | null;
}

export interface IReportees {
  employeeId: string | number;
  firstName: string;
  isExecutive: boolean;
  lastName: string;
  organizationId: number;
}
export interface IManagerAndReportees extends IReportees {
  manager: IReportees | undefined;
  reportees: IReportees[];
}

export interface ITopCategories {
  skills: ISkill[];
  behavioralCompetencies: IBehavioralCompetencies[];
}

export interface IBehavioralCompetencies {
  name: string;
  id?: number;
}